import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { compact, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import ListTable from 'modules/ListTable';
import * as Cells from 'modules/ListTable/cells';
import RestoreAuditLogCell from 'modules/old/cells/RestoreAuditLogCell';
import { getTimeZone } from '_entities/Auth/Auth.selectors';
import { URLS } from '_constants';

const rowKey = (row, index) => `${row.id ?? row.record_id}-${index}`;

/* Same format and empty handling as the legacy TimeCell with isFullDate. */
const FULL_DATE = 'MM/DD/YYYY, hh:mm A';
const formatDate = (value, timeZone) => {
  if (isEmpty(value) || value === 'Invalid date') return '-';
  const m = timeZone ? moment(value).tz(timeZone) : moment(value);
  return m.format(FULL_DATE);
};

/**
 * New-design audit log table: the shared <ListTable /> card, sortable headers, the app's link
 * colour and the Columns picker, in place of the legacy react-table grid.
 *
 * Sort keys are unchanged, so the `ordering` params the API receives are identical — this is a
 * presentation swap. `isShortVersion` still drops the Object type / Record ID columns and the
 * restore action, which is how the retailer and visit tabs render it (the full audit page keeps
 * them). The legacy <AuditLogsTable /> stays for the old design.
 */
const AuditLogsTableNew = ({ data, sortOption, sortHandler, handleAuditAction, isShortVersion, toolbar, footer }) => {
  const timeZone = useSelector(getTimeZone);

  const columns = useMemo(
    () =>
      compact([
        {
          key: 'user__email',
          label: 'User ID',
          sortKey: 'user__email',
          width: '18%',
          // The two identifying columns can't be hidden, matching `disabled: true` in ./constants.
          locked: true,
          render: (row) => <Cells.NameLink to={`${URLS.users}/${row.user__id}`}>{row.user__email}</Cells.NameLink>,
        },
        {
          key: 'user__agency_default__name',
          label: 'Agency',
          sortKey: 'user__agency_default__name',
          locked: true,
          render: (row) => <Cells.Text>{row.user__agency_default__name || '-'}</Cells.Text>,
        },
        {
          key: 'full_name',
          label: 'User Name',
          sortKey: 'full_name',
          render: (row) => <Cells.Text>{row.full_name}</Cells.Text>,
        },
        !isShortVersion && {
          key: 'object_type',
          label: 'Object Type',
          sortKey: 'object_type',
          render: (row) => <Cells.Text>{row.object_type}</Cells.Text>,
        },
        !isShortVersion && {
          key: 'record_id',
          label: 'Record ID',
          sortKey: 'record_id',
          render: (row) => <Cells.Text>{row.record_id}</Cells.Text>,
        },
        {
          key: 'field_name',
          label: 'Field Name',
          sortKey: 'field_name',
          render: (row) => <Cells.Text>{row.field_name}</Cells.Text>,
        },
        {
          key: 'action_flag',
          label: 'Action type',
          sortKey: 'action_flag',
          render: (row) => <Cells.Text>{row.action_flag}</Cells.Text>,
        },
        {
          key: 'previous_value_display',
          label: 'Previous Value',
          sortKey: 'previous_value_display',
          render: (row) => <Cells.Text>{row.previous_value_display}</Cells.Text>,
        },
        {
          key: 'new_value_display',
          label: 'New Field Value',
          sortKey: 'new_value_display',
          render: (row) => <Cells.Text>{row.new_value_display}</Cells.Text>,
        },
        {
          key: 'action_datetime',
          label: 'Audit Log Date',
          sortKey: 'action_datetime',
          render: (row) => <Cells.Text>{formatDate(row.action_datetime, timeZone)}</Cells.Text>,
        },
        {
          key: 'submitted',
          label: 'Submitted',
          sortKey: 'submitted',
          render: (row) => <Cells.Text>{formatDate(row.submitted, timeZone)}</Cells.Text>,
        },
        {
          key: 'import_action',
          label: 'Entry Type',
          sortKey: 'import_action',
          render: (row) => <Cells.Text>{row.import_action}</Cells.Text>,
        },
        !isShortVersion && {
          // No `label`: an action column, so it is never hidden and <MobileRows /> puts it in the
          // card's action strip.
          key: 'restore',
          width: '56px',
          center: true,
          render: (row) => (
            <Cells.Actions>
              <RestoreAuditLogCell original={row} auditAction={handleAuditAction} />
            </Cells.Actions>
          ),
        },
      ]),
    [isShortVersion, timeZone, handleAuditAction],
  );

  return (
    <ListTable
      columns={columns}
      data={data || []}
      rowKey={rowKey}
      toolbar={toolbar}
      footer={footer}
      sortOption={sortOption}
      sortHandler={sortHandler}
      emptyText="No audit log entries found."
      showColumnPicker
      scrollable
      minWidth="1100px"
    />
  );
};

AuditLogsTableNew.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  sortOption: PropTypes.shape({ sortBy: PropTypes.string, sortOrder: PropTypes.number }).isRequired,
  sortHandler: PropTypes.func.isRequired,
  handleAuditAction: PropTypes.func,
  isShortVersion: PropTypes.bool,
  /* Toolbar slots for the table's own header row — the search field and the timezone note live
     there in the new design, rather than floating above the card. */
  toolbar: PropTypes.object,
  footer: PropTypes.node,
};

AuditLogsTableNew.defaultProps = {
  data: [],
  handleAuditAction: undefined,
  isShortVersion: false,
  toolbar: null,
  footer: null,
};

export default memo(AuditLogsTableNew);
